// @ts-check

import { useMutation, useQuery } from "@tanstack/react-query"
import { atom, useAtom } from "jotai"
import { useSelector } from "react-redux"
import { DocumentQueryKeys, DocumentQueryOptions, DocumentMutationOptions } from "./query"
import { serializeCanvasDocuments } from "./serializeCanvasDocuments"

const activeCanvasDocumentIdAtom = atom(/**@type{string|null}*/(null))

function useActiveCanvasDocumentId() {
  return useAtom(activeCanvasDocumentIdAtom)
}

function useUserToken() {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate)
  return user?.userToken ?? null
}

/**
 * @param {string | null | undefined} documentId
 */
function useDocument(documentId) {
  const userToken = useUserToken()
  return useQuery(DocumentQueryOptions.DOCUMENT({ documentId, userToken }))
}

/**
 * @param {string | null | undefined} documentId
 * @param {string | null | undefined} versionId
 */
function useDocumentVersion(documentId, versionId) {
  const userToken = useUserToken()
  return useQuery(DocumentQueryOptions.DOCUMENT_VERSION({ documentId, versionId, userToken }))
}

function useCreateDocument() {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.CREATE_DOCUMENT({ userToken }))
}

/**
 * @param {string} documentId
 */
function useUpdateDocument(documentId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.UPDATE_DOCUMENT({ documentId, userToken }))
}

/**
 * @param {string} documentId
 * @param {string} [sessionId]
 */
function useAcceptDiff(documentId, sessionId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.ACCEPT_DIFF({ documentId, userToken, sessionId }))
}

/**
 * @param {string} documentId
 */
function useRejectDiff(documentId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.REJECT_DIFF({ documentId, userToken }))
}

/**
 * @param {string} documentId
 * @param {string} [sessionId]
 */
function useAcceptAllDiffs(documentId, sessionId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.ACCEPT_ALL_DIFFS({ documentId, userToken, sessionId }))
}

/**
 * @param {string} documentId
 */
function useRejectAllDiffs(documentId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.REJECT_ALL_DIFFS({ documentId, userToken }))
}

/**
 * @param {string} documentId
 */
function useUndo(documentId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.UNDO({ documentId, userToken }))
}

/**
 * @param {string} documentId
 */
function useRedo(documentId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.REDO({ documentId, userToken }))
}

/**
 * @param {string} documentId
 * @param {string} [sessionId]
 */
function useDeleteDocument(documentId, sessionId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.DELETE_DOCUMENT({ documentId, userToken, sessionId }))
}

/**
 * @param {string} documentId
 */
function useExportDocument(documentId) {
  const userToken = useUserToken()
  return useMutation(DocumentMutationOptions.EXPORT({ documentId, userToken }))
}

export {
  DocumentQueryKeys,
  serializeCanvasDocuments,
  useActiveCanvasDocumentId,
  useDocument,
  useDocumentVersion,
  useCreateDocument,
  useUpdateDocument,
  useAcceptDiff,
  useRejectDiff,
  useAcceptAllDiffs,
  useRejectAllDiffs,
  useUndo,
  useRedo,
  useDeleteDocument,
  useExportDocument,
}
